<template>
  <div 
    v-if="visible && currentRow" 
    class="detail-drawer"
    :style="drawerStyle"
    @mousedown="handleMouseDown"
  >
    <div class="drawer-header">
      <h3>证书详情</h3>
      <el-button 
        type="text" 
        icon="el-icon-close" 
        @click="handleClose"
        class="close-btn"
      />
    </div>
    <div class="drawer-content">
      <div class="detail-item">
        <span class="detail-label">证书编号：</span>
        <span class="detail-value">{{ currentRow.certificateCode }}</span>
      </div>
      <div class="detail-item">
        <span class="detail-label">证书单位：</span>
        <span class="detail-value">{{ currentRow.certificateCompanyName }}</span>
      </div>
      <div class="detail-item">
        <span class="detail-label">器具名称：</span>
        <span class="detail-value">{{ currentRow.instrumentName }}</span>
      </div>
      <div class="detail-item">
        <span class="detail-label">型号规格：</span>
        <span class="detail-value">{{ currentRow.modelSpecification || '-' }}</span>
      </div>
      <div class="detail-item">
        <span class="detail-label">出厂编号：</span>
        <span class="detail-value">{{ currentRow.factorySerialCode || '-' }}</span>
      </div>
      <div class="detail-item">
        <span class="detail-label">最近更新时间：</span>
        <span class="detail-value">{{ currentRow.updateTime || '-' }}</span>
      </div>
      
      <!-- 动态展示jsonData内容 -->
      <div v-for="(field, index) in dynamicFields" v-if="dynamicFields && dynamicFields.length > 0" :key="index" class="detail-item">
        <span class="detail-label">{{ field.header }}：</span>
        <span class="detail-value">{{ field.value !== null ? field.value : '' }}</span>
      </div>
      <!-- 可以根据需要添加更多详情字段 -->
    </div>
    <div class="drawer-footer">
      <el-button @click="handleDownload(currentRow)">下载二维码</el-button>
      <el-button type="primary" @click="handleEdit(currentRow)">修改</el-button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import certificateAPI from '@/api/certificate.js'

// 定义props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  currentRow: {
    type: Object,
    default: null
  }
})

// 定义emit
const emit = defineEmits(['close', 'edit', 'download'])

// 拖拽相关状态
const position = ref({ x: window.innerWidth - 400, y: 200 })
const isDragging = ref(false)
const dragOffset = ref({ x: 0, y: 0 })

// 动态字段数据
const dynamicFields = ref([])

// 监听currentRow变化，解析jsonData
watch(() => props.currentRow, (newRow) => {
  if (newRow && newRow.jsonData) {
    try {
      // 解析jsonData字符串为对象数组
      dynamicFields.value = JSON.parse(newRow.jsonData)
    } catch (error) {
      console.error('解析jsonData失败:', error)
      dynamicFields.value = []
    }
  } else {
    dynamicFields.value = []
  }
}, { immediate: true, deep: true })

// 计算抽屉样式
const drawerStyle = computed(() => {
  return {
    transform: `translate(${position.value.x}px, ${position.value.y}px)`
  }
})

// 处理鼠标按下事件（开始拖拽）
const handleMouseDown = (e) => {
  // 只有点击头部才允许拖拽
  if (!e.target.closest('.drawer-header')) {
    return
  }
  
  isDragging.value = true
  dragOffset.value = {
    x: e.clientX - position.value.x,
    y: e.clientY - position.value.y
  }
  
  // 添加鼠标移动和释放事件监听器
  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
  
  // 阻止默认行为和事件冒泡
  e.preventDefault()
  e.stopPropagation()
}

// 处理鼠标移动事件（拖拽中）
const handleMouseMove = (e) => {
  if (!isDragging.value) return
  
  // 计算新的位置，确保不会超出可视区域
  const newX = Math.max(0, Math.min(e.clientX - dragOffset.value.x, window.innerWidth - 350))
  const newY = Math.max(0, Math.min(e.clientY - dragOffset.value.y, window.innerHeight - 450))
  
  position.value = { x: newX, y: newY }
}

// 处理鼠标释放事件（结束拖拽）
const handleMouseUp = () => {
  isDragging.value = false
  // 移除事件监听器
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
}

// 处理关闭抽屉
const handleClose = () => {
  emit('close')
}

// 处理编辑
const handleEdit = (row) => {
  emit('edit', row)
}

// 处理下载二维码
const handleDownload = async (row) => {
  try {
    const response = await certificateAPI.downloadQrCode(row.id)
    const blob = response
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `二维码_${row.certificateCode}.png`
    link.click()
    window.URL.revokeObjectURL(url)
    ElMessage.success('二维码下载成功')
  } catch (error) {
    // 直接显示API返回的错误消息
    ElMessage.error(error.message || '下载失败，请稍后重试')
    console.error('下载二维码失败:', error)
  }
}
</script>

<style scoped>
.detail-drawer {
  position: fixed;
  width: 350px;
  max-height: 450px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;
}

.drawer-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: move;
  background-color: #fafafa;
  border-radius: 8px 8px 0 0;
}

.drawer-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.close-btn {
  color: var(--text-secondary);
}

.drawer-content {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.detail-item {
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
}

.detail-label {
  min-width: 100px;
  color: #909399;
  font-size: 14px;
}

.detail-value {
  flex: 1;
  color: #303133;
  font-size: 14px;
  word-break: break-word;
}

.drawer-footer {
  padding: 16px 20px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 动态字段样式 */
.dynamic-fields-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .detail-drawer {
    width: 300px;
    max-height: 400px;
  }
}
</style>